import { useEffect, useState } from "react";
import { GalleryHorizontal, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { staffApi, errMsg } from "@/lib/api";
import { loadDisplay } from "@/lib/display";

const SECTIONS = [["communities", "Communities"], ["serve", "Serve Ministry"], ["pastoral", "Pastoral Care"]];

const NumPick = ({ value, options, onChange, testId, suffix = "" }) => (
  <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
    <SelectTrigger className="w-28 bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white">{options.map((n) => <SelectItem key={n} value={String(n)}>{n}{suffix}</SelectItem>)}</SelectContent>
  </Select>
);

const SectionRow = ({ id, label, s, set }) => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl bg-[#F8FAFC] p-4" data-testid={`display-row-${id}`}>
    <p className="w-full font-medium text-slate-900 sm:w-36">{label}</p>
    <label className="flex items-center gap-2 text-sm text-slate-600">Pictures at a time
      <NumPick value={s.count} options={[1, 2, 3, 4, 5, 6]} onChange={(count) => set({ ...s, count })} testId={`display-count-${id}`} />
    </label>
    <label className="flex items-center gap-2 text-sm text-slate-600">
      <Switch checked={s.auto} onCheckedChange={(auto) => set({ ...s, auto })} data-testid={`display-auto-${id}`} /> Auto-scroll
    </label>
    {s.auto && (
      <label className="flex items-center gap-2 text-sm text-slate-600">Every
        <NumPick value={s.seconds} options={[2, 3, 4, 5, 6, 8, 10, 15]} suffix=" sec" onChange={(seconds) => set({ ...s, seconds })} testId={`display-seconds-${id}`} />
      </label>
    )}
  </div>
);

export const DisplaySettingsCard = () => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { loadDisplay(true).then(setF); }, []);
  if (!f?.communities) return null;
  const save = async () => {
    setBusy(true);
    try { await staffApi().put("/display-settings", f); await loadDisplay(true); toast.success("Display settings saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="display-settings-card">
      <div className="flex items-center gap-2"><GalleryHorizontal className="h-5 w-5 text-[#1D64B4]" /><h3 className="font-semibold text-slate-900">Picture rows</h3></div>
      <p className="mt-1 text-sm text-slate-500">How many pictures show side by side on a computer, and whether they slide on their own. Sliding pauses while a visitor touches or hovers over it, and they can also swipe by hand.</p>
      <div className="mt-4 space-y-3">
        {SECTIONS.map(([id, label]) => <SectionRow key={id} id={id} label={label} s={f[id]} set={(v) => setF({ ...f, [id]: v })} />)}
      </div>
      <Button onClick={save} disabled={busy} className="mt-4 rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="display-settings-save">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save display settings
      </Button>
    </div>
  );
};
